'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { buildTrackingId } from '@/utils/tracking-helper';
import Container from '../../shared/Container';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { DynamicComparisonTableTile } from './types/DynamicComparisonTableTile';
import ProductInfo from './components/ProductInfo/product-info';

export default function DynamicComparisonTable({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: DynamicComparisonTableTile;
  trackingPrefix?: string;
}>) {
  const { localizedLink } = useLocaleLink();
  const { title } = tile;
  const { description } = tile;
  const link = extractComponentFromChunk(
    tile.link?.chunks?.[0],
    'internal-link',
    'ItemRelationsContent',
  )?.items?.[0].path;
  const linkTextContent = tile.link?.chunks?.[0].find((c) => c.id === 'text')?.content;
  const linkText =
    linkTextContent?.__typename === 'SingleLineContent' ? linkTextContent.text : undefined;
  const initialProduct1 = tile['initial-product-1']?.number;
  const initialProduct2 = tile['initial-product-2']?.number;

  const headerLinkTrackingId = buildTrackingId(trackingPrefix, linkText);

  return (
    <Container>
      <div className=" text-center text-2xl sm:text-3xl lg:text-4xl">
        <RichTextRenderer blocks={title} />
        <div className="mt-4 text-md sm:mt-6 sm:text-lg">
          <RichTextRenderer blocks={description} />
          {link && linkText && (
            <Link
              href={localizedLink(link)}
              className=" mx-auto flex w-fit items-center underline sm:gap-1"
              data-tile-element-id={headerLinkTrackingId}
            >
              {linkText}
              <ChevronRightIcon className="h-auto w-5 text-primary-100" />
            </Link>
          )}
        </div>
      </div>
      <div className=" mx-auto mt-6 grid max-w-4xl grid-cols-2 gap-x-4 sm:gap-x-8 md:mt-12">
        {tile.products && (
          <>
            <ProductInfo
              tile={tile}
              initialProductIdx={initialProduct1}
              trackingPrefix={trackingPrefix}
            />
            <ProductInfo
              tile={tile}
              initialProductIdx={initialProduct2}
              trackingPrefix={trackingPrefix}
            />
          </>
        )}
      </div>
    </Container>
  );
}
